import { CdkConnectedOverlay, ConnectedPosition, Overlay, ScrollStrategy } from '@angular/cdk/overlay';
import {
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import { CustomControlValueAccessor } from '../../utils/custom-control-value-accessor/custom-control-value-accessor';
import {
  CSS_AUTOCOMPLETE_ITEM_SELECTOR,
  CSS_DROPDOWN_CLASS,
  CSS_HIGHLIGHTED_CLASS,
  CSS_SCROLL_BEHAVIOR,
  DEBOUNCE_TIME,
  FIRST_ITEM_INDEX,
  INITIAL_HIGHLIGHTED_INDEX,
  KEY_ACTION_MAP,
  MIN_TEXT_LENGTH,
  NAVIGATION_INCREMENT,
  OVERLAY_PANEL_CLASS,
  SCROLL_TOP_POSITION
} from './autocomplete-constants';
import { EnumKeyboardAction } from './enums/keyboard-action.interface';
import { IAutocompleteSelectedItem } from './interface/autocomplete-item.interface';
import { IAutocompleteSettings } from './interface/settings.interface';

@Component({
  selector: 'sgt-autocomplete',
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteComponent),
      multi: true,
    },
  ],
})
export class AutocompleteComponent extends CustomControlValueAccessor<string> implements OnInit, DoCheck, OnDestroy {
  @Input() selectedItem!: IAutocompleteSelectedItem | null;
  @Input() settings!: IAutocompleteSettings;

  @ViewChild('inputGroup') inputGroup!: ElementRef<HTMLElement>;

  @ViewChild(CdkConnectedOverlay) connectedOverlay!: CdkConnectedOverlay;

  @ViewChild('dropdown', { static: false })
  dropdown!: ElementRef<HTMLUListElement>;

  searchText = '';
  highlightedIndex: number = INITIAL_HIGHLIGHTED_INDEX;

  readonly overlayPanelClass = OVERLAY_PANEL_CLASS;

  overlayWidth = 0;
  readonly scrollStrategy: ScrollStrategy;
  readonly overlayPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ];

  private searchText$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  @Output() changed = new EventEmitter<string | null>();

  constructor(private elementRef: ElementRef<HTMLElement>, private overlay: Overlay) {
    super();
    this.scrollStrategy = this.overlay.scrollStrategies.reposition();
  }

  ngDoCheck(): void {
    if (this.settings?.clearFields) {
      this.clearSelection();
    }

    if (!this.selectedItem && !this.searchText && this.settings) {
      this.settings.isEmpty = null;
    }

    if (this.settings?.isOpenDropdown) {
      this.atualizarLargura();
    }
  }

  @HostListener('window:resize')
  onViewportChange(): void {
    if (this.settings?.isOpenDropdown) {
      this.atualizarLargura();
    }
  }

  onOverlayDetach(): void {
    this.dropdown = undefined;
  }

  onOverlayOutsideClick(event: MouseEvent): void {
    const alvo = event.target as Element;
    if (this.elementRef.nativeElement.contains(alvo)) {
      return;
    }
    if (alvo?.closest?.(`.${this.overlayPanelClass}`)) {
      return;
    }
    if (this.settings) {
      this.settings.isOpenDropdown = false;
    }
    this.highlightedIndex = INITIAL_HIGHLIGHTED_INDEX;
  }

  private atualizarLargura(): void {
    if (!this.inputGroup) {
      return;
    }
    const largura = this.inputGroup.nativeElement.getBoundingClientRect().width;
    if (largura && largura !== this.overlayWidth) {
      this.overlayWidth = largura;
    }
  }

  private get dropdownEl(): HTMLUListElement | null {
    if (this.dropdown) {
      return this.dropdown.nativeElement;
    }
    const painel = this.connectedOverlay?.overlayRef?.overlayElement;
    return painel?.querySelector<HTMLUListElement>(`.${CSS_DROPDOWN_CLASS}`) ?? null;
  }

  // Implementação do método abstrato da classe base
  protected updateValue(value: string | null): void {
    this.searchText = value || '';
  }

  // Override do onDisabledStateChange para lógica específica
  protected onDisabledStateChange(isDisabled: boolean): void {
    if (this.settings) {
      this.settings.disabled = isDisabled;
    }
  }

  ngOnInit(): void {
    this.watchingSearchText();
  }

  private watchingSearchText(): void {
    this.searchText$
      .pipe(
        debounceTime(DEBOUNCE_TIME),
        filter((text) => text.length >= MIN_TEXT_LENGTH),
        takeUntil(this.destroy$)
      )
      .subscribe((searchText) => {
        if (!searchText.length && this.selectedItem) {
          this.clearSelection();
        }
        this.changed.emit(searchText);
        this.emitChange(searchText);
        this.markAsTouched();
      });
  }

  onModelChange(value: string): void {
    this.searchText = value;
    this.highlightedIndex = INITIAL_HIGHLIGHTED_INDEX;

    const dropdownEl = this.dropdownEl;
    if (dropdownEl) {
      dropdownEl.scrollTop = SCROLL_TOP_POSITION;
    }

    if (!value.trim().length) {
      this.settings.isEmpty = null;
      this.clearSelection();
      return;
    }

    this.searchText$.next(value);
  }

  clearSelection(): void {
    this.selectedItem = null;
    this.searchText = null;
    this.highlightedIndex = INITIAL_HIGHLIGHTED_INDEX;
    this.settings.isEmpty = null;
    this.settings.clearFields = false;
    this.updateHighlight();
    this.changed.emit(null);
    this.emitChange(null);
    this.markAsTouched();
  }

  shouldShowNoResults(): boolean {
    return (
      this.settings?.isEmpty &&
      !this.settings?.isLoading &&
      this.searchText?.trim().length >= MIN_TEXT_LENGTH
    );
  }

  private navigateDown(itemsLength: number): void {
    if (this.highlightedIndex < itemsLength - NAVIGATION_INCREMENT) {
      this.highlightedIndex++;
    } else {
      this.highlightedIndex = FIRST_ITEM_INDEX;
    }
    this.updateHighlight();
  }

  private navigateUp(itemsLength: number): void {
    if (this.highlightedIndex > FIRST_ITEM_INDEX) {
      this.highlightedIndex--;
    } else {
      this.highlightedIndex = itemsLength - NAVIGATION_INCREMENT;
    }
    this.updateHighlight();
  }

  private updateHighlight(): void {
    const dropdownEl = this.dropdownEl;
    if (!dropdownEl) {
      return;
    }

    const items = dropdownEl.querySelectorAll(
      CSS_AUTOCOMPLETE_ITEM_SELECTOR
    );

    items.forEach((item) => {
      item.classList.remove(CSS_HIGHLIGHTED_CLASS);
    });

    if (
      this.highlightedIndex >= FIRST_ITEM_INDEX &&
      this.highlightedIndex < items.length
    ) {
      const highlightedItem = items[this.highlightedIndex] as HTMLElement;
      highlightedItem.classList.add(CSS_HIGHLIGHTED_CLASS);

      this.scrollToHighlightedItem(highlightedItem);
    }
  }

  private scrollToHighlightedItem(item: HTMLElement): void {
    const dropdown = this.dropdownEl;
    if (!dropdown) {
      return;
    }
    const itemTop = item.offsetTop;
    const itemBottom = itemTop + item.offsetHeight;
    const dropdownTop = dropdown.scrollTop;
    const dropdownBottom = dropdownTop + dropdown.clientHeight;

    if (itemTop < dropdownTop) {
      dropdown.scrollTo({
        top: itemTop,
        behavior: CSS_SCROLL_BEHAVIOR as ScrollBehavior,
      });
    } else if (itemBottom > dropdownBottom) {
      dropdown.scrollTo({
        top: itemBottom - dropdown.clientHeight,
        behavior: CSS_SCROLL_BEHAVIOR as ScrollBehavior,
      });
    }
  }

  private selectHighlightedItem(): void {
    const dropdownEl = this.dropdownEl;
    if (!dropdownEl) {
      return;
    }

    const items = dropdownEl.querySelectorAll(
      CSS_AUTOCOMPLETE_ITEM_SELECTOR
    );

    if (
      items &&
      this.highlightedIndex >= FIRST_ITEM_INDEX &&
      this.highlightedIndex < items.length
    ) {
      const selectedElement = items[this.highlightedIndex] as HTMLElement;
      selectedElement.click();
      this.highlightedIndex = INITIAL_HIGHLIGHTED_INDEX;
      this.updateHighlight();
    }
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const dropdownEl = this.dropdownEl;
    if (!this.settings?.isOpenDropdown || !dropdownEl) {
      return;
    }

    const items = dropdownEl.querySelectorAll(
      CSS_AUTOCOMPLETE_ITEM_SELECTOR
    );

    if (!items || items.length === FIRST_ITEM_INDEX) {
      return;
    }

    const action = KEY_ACTION_MAP.get(event.key);
    if (!action) {
      return;
    }

    event.preventDefault();
    this.keyboardAction(action, items.length);
  }

  private keyboardAction(action: EnumKeyboardAction, itemsLength: number): void {
    const actionHandlers = {
      [EnumKeyboardAction.NAVIGATE_DOWN]: () => this.navigateDown(itemsLength),
      [EnumKeyboardAction.NAVIGATE_UP]: () => this.navigateUp(itemsLength),
      [EnumKeyboardAction.SELECT_ITEM]: () => this.handleSelectAction(itemsLength),
      [EnumKeyboardAction.CLEAR_HIGHLIGHT]: () => this.clearHighlight()
    };

    const handler = actionHandlers[action];
    if (handler) {
      handler();
    }
  }

  private handleSelectAction(itemsLength: number): void {
    if (
      this.highlightedIndex >= FIRST_ITEM_INDEX &&
      this.highlightedIndex < itemsLength
    ) {
      this.selectHighlightedItem();
    }
  }

  private clearHighlight(): void {
    this.highlightedIndex = INITIAL_HIGHLIGHTED_INDEX;
    this.updateHighlight();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
